/**
 * `DIALOG.easingPicker` (W2-05 easing-templates-raw; DESIGN.md §4.12, §5.7; U§5 A5): choosing a curve fast for both
 * audiences. Resolves with the chosen Ease (the CALLER applies it with `Core.keys.setEase`), or null on Esc.
 *
 *  - Grid of plain-word curves (EasingGrid), Phira's formulas in every thumbnail; RPE numbers at the Full level or on
 *    hover; 23 / 29 marked as drawn differently by Phira; QuintInOut / ExpoInOut as fitted Bezier curves with error.
 *  - Live preview: hovering or focusing a curve plays the REAL segment(s) with it in the preview (a temporary
 *    gesture, cancelled when the picker closes) and draws the value over time; nothing changes until Enter / click.
 *  - Keyboard: arrows + Enter; an RPE number (two digits within 400 ms); `/` search; `B` custom curve (Full: focus
 *    moves to its first handle, Enter there picks the curve, B again returns to the grid); `Tab` to the
 *    part-of-the-curve fields (Full); `F` favourite; Esc cancels. Focus never drops out of the picker when a tab
 *    switch unmounts the focused control, so Esc (the popover surface) keeps working.
 *  - Experience level (DESIGN.md level table): the Guided level shows the plain-word grid (with the fitted Quint /
 *    Expo cells); RPE numbers, the part-of-the-curve handles and the custom curve tab are Full only.
 *  - Context guards per EaseUse: refused curves are disabled with the reason (speed at the Guided level: Linear and
 *    Stay only; text typing / erasing: no overshoot; note controls: numbered curves only); custom curves are hidden
 *    for speed, note controls and effect / video values (where "Bake" is offered instead); eased legacy speed is
 *    badged "baked on export"; text overshoot elsewhere carries a warning.
 *  - Several segments: the footer says how many take the curve and why the others are skipped.
 *  - Recent curves and favourites live in `settings.ext` ('easing.recents', 'easing.favourites').
 */

import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import type { DialogProps } from '../../app/registry/types';
import { Core } from '../../core/commands/index';
import { LINEAR } from '../../core/ease/index';
import type { BezierPoints, Ease as EaseValue } from '../../core/ease/types';
import type { LineId } from '../../core/model/types';
import { Badge, Button, CurveThumb, Icon, IconButton, Tabs } from '../../ui';
import { useReducedMotion } from '../../ui/internal/useReducedMotion';
import { enterHasOwnMeaning } from './applyKeys';
import { BezierEditor, collisionsIn } from './BezierEditor';
import { formatCubicBezier } from './bezierModel';
import { CurvePreview } from './CurvePreview';
import { m } from './easing.messages';
import s from './Easing.module.css';
import { cellName, EasingGrid } from './EasingGrid';
import { useDocVersion, useEditorSlice, useServicesStore, useSettings, useText, type Text } from './hooks';
import { createLivePreview, createProgressRelay, type LivePreview } from './livePreview';
import {
  applyCount,
  bezierAllowed,
  cellAt,
  cellById,
  cellForEase,
  easeKey,
  easeList,
  easeState,
  easeToJson,
  FAVOURITES_KEY,
  moveFocus,
  pushRecent,
  RECENTS_KEY,
  sameEase,
  searchCells,
  segmentInfos,
  sliceAllowed,
  toggleFavourite,
  typeDigit,
  wholeCurve,
  type DigitState,
  type NavKey,
  type PickerCell,
} from './pickerModel';
import { formatValue, segmentPreview } from './segmentPreview';
import { SliceEditor } from './SliceEditor';

const NAV_KEYS: ReadonlySet<string> = new Set([
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'Home',
  'End',
  'PageUp',
  'PageDown',
]);

/** Name of any ease (grid cells by their words, slices and Bezier curves described). */
export function easeName(e: EaseValue, text: Text): string {
  const cell = cellForEase(e);
  if (e.kind === 'bezier')
    return cell === null ? `${text.t(m.tabBezier)} ${formatCubicBezier(e.p)}` : cellName(cell, text);
  if (cell === null) return text.t(m.linear);
  const name = cellName(cell, text);
  if (e.kind === 'tween' && (e.left !== 0 || e.right !== 1)) {
    return `${name} (${text.t(m.sliceRange, { left: e.left.toFixed(2), right: e.right.toFixed(2) })})`;
  }
  return name;
}

function isTextTarget(el: Element): boolean {
  return el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || (el instanceof HTMLElement && el.isContentEditable);
}

type Tab = 'curves' | 'bezier';
/** Where keyboard focus goes after the next render (a tab switch unmounts the focused control). */
type FocusTarget = 'cell' | 'handle' | 'search';

export function EasingPicker(props: DialogProps<'easingPicker'>) {
  const { services } = props;
  const { segments, initial, use } = props.props;
  const text = useText();
  const { t, tr } = text;
  const store = useServicesStore(services);
  useDocVersion(store);
  const level = useEditorSlice(services, (st) => st.level);
  const settings = useSettings(services);
  const reduced = useReducedMotion();
  const ext = settings.ext;
  const showIds = level === 'full' || ext['easing.showIds'] === true;
  const favourites = useMemo(() => easeList(ext[FAVOURITES_KEY]), [ext]);
  const recents = useMemo(() => easeList(ext[RECENTS_KEY]), [ext]);
  /** Custom curves are allowed for this use; the tab itself is a Full-level tool. */
  const bezierUseOk = bezierAllowed(use);
  const bezierOk = bezierUseOk && level === 'full';
  const titleId = useId();
  const listId = useId();

  const startCell: PickerCell = cellForEase(initial) ??
    cellAt(-1, 0) ?? { id: 'linear', ease: LINEAR, entry: null, row: -1, col: 0 };
  const [focus, setFocus] = useState({ row: startCell.row, col: startCell.col });
  const [selected, setSelected] = useState<EaseValue>(initial ?? LINEAR);
  const [hovered, setHovered] = useState<EaseValue | null>(null);
  /** The user has chosen something (moved, typed, edited): until then only hovering previews. */
  const [touched, setTouched] = useState(false);
  const [tabState, setTab] = useState<Tab>(
    initial?.kind === 'bezier' && cellForEase(initial) === null && bezierOk ? 'bezier' : 'curves',
  );
  const tab: Tab = bezierOk ? tabState : 'curves';
  const [bezier, setBezier] = useState<BezierPoints>(initial?.kind === 'bezier' ? initial.p : [0.25, 0.1, 0.25, 1]);
  const [snap, setSnap] = useState(true);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const digits = useRef<DigitState | null>(null);
  const cellEls = useRef(new Map<string, HTMLButtonElement>());
  const focusWanted = useRef<FocusTarget | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const live = useRef<LivePreview | null>(null);
  const [progress] = useState(createProgressRelay);
  /**
   * Hover previews start once the pointer really moved over the picker (UX-044): it opens under the double-click,
   * and the cell that happens to be under the resting pointer must not apply itself to the segment at once.
   */
  const hoverArmed = useRef(false);
  const firstPointer = useRef<{ x: number; y: number } | null>(null);
  const hover = (e: EaseValue | null): void => {
    if (e !== null && !hoverArmed.current) return;
    setHovered(e);
  };

  // Fit the window: the surface keeps its place, the body scrolls so the footer stays reachable (also on resize).
  useLayoutEffect(() => {
    const el = rootRef.current;
    if (el === null) return undefined;
    const fit = (): void => {
      // The anchored surface around the picker keeps the place it got when it opened; after a resize, move it
      // back inside the window (the host places it only once).
      const surface = el.closest<HTMLElement>('[role="dialog"]');
      if (surface !== null && surface.style.position !== 'static') {
        const r = surface.getBoundingClientRect();
        const margin = 8;
        if (r.right > window.innerWidth - margin) {
          surface.style.left = `${String(Math.max(margin, window.innerWidth - margin - r.width))}px`;
        }
        if (r.bottom > window.innerHeight - margin && r.top > margin) {
          surface.style.top = `${String(Math.max(margin, window.innerHeight - margin - r.height))}px`;
        }
      }
      const top = Math.max(0, el.getBoundingClientRect().top);
      el.style.setProperty('--picker-max-h', `${String(Math.max(240, window.innerHeight - top - 8))}px`);
    };
    fit();
    // The host places the surface after its first layout: fit again on the next frame.
    const raf = requestAnimationFrame(fit);
    window.addEventListener('resize', fit);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', fit);
    };
  }, []);

  const infos = store === null ? [] : segmentInfos(store.doc, store.idx, segments, level);
  const first = segments[0];
  const info = store === null || first === undefined ? null : segmentPreview(store.doc, store.idx, first);
  const results = useMemo(() => searchCells(query), [query]);
  const bezierEase = useMemo<EaseValue>(() => ({ kind: 'bezier', p: bezier }), [bezier]);
  const chosen = tab === 'bezier' ? bezierEase : selected;
  const candidate = hovered ?? chosen;
  const collision = tab === 'bezier' ? collisionsIn(store, bezier, segments) : null;

  // Live preview: one gesture for the picker's lifetime, cancelled on close.
  useEffect(() => {
    const preview = createLivePreview(services, {
      meta: { label: { key: m.txSetCurve, params: { name: '' } } },
      loop: !reduced,
      onLoopChange: progress.notify,
    });
    live.current = preview;
    progress.attach(preview);
    return () => {
      preview.end();
      live.current = null;
      progress.attach(null);
    };
  }, [services, reduced, progress]);

  const t0 = info?.span.t0 ?? null;
  const t1 = info?.span.t1 ?? null;
  useEffect(() => {
    const preview = live.current;
    if (preview === null || segments.length === 0) return;
    const span = t0 === null || t1 === null ? null : { t0, t1 };
    const untouched = hovered === null && !touched;
    if (
      untouched ||
      (initial !== null && sameEase(candidate, initial)) ||
      easeState(candidate, use).kind === 'disabled'
    ) {
      preview.show(null, span);
      return;
    }
    preview.show((tx) => Core.keys.setEase(tx, { segments, ease: candidate }), span);
  }, [candidate, hovered, touched, initial, segments, t0, t1, use]);

  // Keyboard focus follows the focused cell, and lands in the shown tab after a keyboard tab switch (every render:
  // the target exists only once the new tab rendered).
  useEffect(() => {
    const want = focusWanted.current;
    if (want === null) return;
    focusWanted.current = null;
    if (want === 'handle') {
      rootRef.current?.querySelector<SVGElement>('[data-handle="0"]')?.focus();
      return;
    }
    if (want === 'search') {
      searchRef.current?.focus();
      return;
    }
    const cell = cellAt(focus.row, focus.col);
    if (cell !== null) cellEls.current.get(cell.id)?.focus();
  });

  const finish = (result: EaseValue | null): void => {
    live.current?.end();
    if (result !== null) services.settings.setExt(RECENTS_KEY, pushRecent(recents, result).map(easeToJson));
    props.close(result);
  };

  const pick = (e: EaseValue): void => {
    if (easeState(e, use).kind === 'disabled') return;
    finish(e);
  };

  /** Shows a tab; `keyboard`: focus moves into it (the focused control of the other tab goes away). */
  const showTab = (next: Tab, keyboard: boolean): void => {
    setTab(next);
    setTouched(true);
    if (keyboard) focusWanted.current = next === 'bezier' ? 'handle' : query !== '' ? 'search' : 'cell';
  };

  /** Moves the focus (and the selection) to a cell. */
  const goTo = (cell: PickerCell, keyboard: boolean): void => {
    setTouched(true);
    focusWanted.current = keyboard ? 'cell' : null;
    setFocus({ row: cell.row, col: cell.col });
    setTab('curves');
    setQuery('');
    const keepSlice = selected.kind === 'tween' && cell.ease.kind === 'tween' && selected.id === cell.ease.id;
    setSelected(keepSlice ? selected : cell.ease);
  };

  const favouriteToggle = (e: EaseValue): void => {
    services.settings.setExt(FAVOURITES_KEY, toggleFavourite(favourites, wholeCurve(e)).map(easeToJson));
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    const target = e.target as Element;
    if (target === searchRef.current) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const n = results.length;
        if (n > 0) setActive((i) => (e.key === 'ArrowDown' ? (i + 1) % n : (i - 1 + n) % n));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const hit = results[active] ?? results[0];
        if (hit !== undefined) pick(hit.ease);
      } else if (e.key === 'Escape' && query !== '') {
        e.preventDefault();
        e.stopPropagation();
        setQuery('');
      }
      return;
    }
    if (isTextTarget(target)) return;
    if ((e.key === 'b' || e.key === 'B') && bezierOk) {
      e.preventDefault();
      showTab(tab === 'bezier' ? 'curves' : 'bezier', true);
      return;
    }
    // Handles (Bezier) and part-of-the-curve thumbs own their arrow keys; Enter picks what they shape.
    if (e.key === 'Enter' && target.getAttribute('role') === 'slider') {
      e.preventDefault();
      pick(tab === 'bezier' ? bezierEase : selected);
      return;
    }
    if (e.key === '/') {
      e.preventDefault();
      setTab('curves');
      searchRef.current?.focus();
      return;
    }
    if (e.key === 'f' || e.key === 'F') {
      e.preventDefault();
      favouriteToggle(chosen);
      return;
    }
    if (/^\d$/.test(e.key)) {
      e.preventDefault();
      const r = typeDigit(digits.current, e.key, e.timeStamp);
      digits.current = r.state;
      if (r.easingType !== null) {
        const cell = r.easingType === 1 ? cellById('linear') : cellById(`rpe:${String(r.easingType)}`);
        if (cell !== null) goTo(cell, true);
      }
      return;
    }
    if (tab === 'bezier') {
      if (e.key === 'Enter' && !enterHasOwnMeaning(target)) {
        e.preventDefault();
        pick(bezierEase);
      }
      return;
    }
    if (!(target instanceof HTMLElement) || target.dataset.cell === undefined) return;
    if (NAV_KEYS.has(e.key)) {
      e.preventDefault();
      const next = moveFocus(focus.row, focus.col, e.key as NavKey);
      const cell = cellAt(next.row, next.col);
      if (cell !== null) goTo(cell, true);
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      pick(selected);
    }
  };

  const stateHint =
    use === 'speedLegacyGuided'
      ? m.speedGuidedHint
      : use === 'textRevealErase'
        ? m.revealHint
        : use === 'control'
          ? m.controlHint
          : null;
  const count = applyCount(infos, candidate);
  const bezierState = easeState(bezierEase, use);
  const chosenState = easeState(chosen, use);
  const candState = easeState(candidate, use);
  const showSlice = level === 'full' && tab === 'curves';
  const sliceEase = selected.kind === 'tween' ? selected : null;
  const sliceReason =
    sliceEase === null || !sliceAllowed(use, sliceEase)
      ? use === 'control'
        ? { key: m.sliceControl }
        : sliceEase !== null && sliceEase.id === 1
          ? { key: m.sliceLinear }
          : { key: m.sliceNotTween }
      : null;
  const isFav = (e: EaseValue): boolean => favourites.some((f) => sameEase(f, wholeCurve(e)));
  const lineName = (line: LineId): string => store?.doc.lines.get(line)?.name ?? String(line);

  const chip = (e: EaseValue, key: string) => {
    const state = easeState(e, use);
    return (
      <button
        key={key}
        type="button"
        className={s.chip}
        aria-label={easeName(e, text)}
        title={state.kind === 'disabled' ? `${easeName(e, text)}\n${tr(state.reason)}` : easeName(e, text)}
        aria-disabled={state.kind === 'disabled' || undefined}
        onPointerEnter={() => {
          hover(e);
        }}
        onPointerLeave={() => {
          hover(null);
        }}
        onClick={() => {
          pick(e);
        }}
      >
        <CurveThumb ease={e} size={24} animate={false} />
      </button>
    );
  };

  return (
    <div
      ref={rootRef}
      className={s.picker}
      onKeyDown={onKeyDown}
      aria-labelledby={titleId}
      onPointerMove={(e) => {
        if (!hoverArmed.current) {
          const p0 = firstPointer.current;
          if (p0 === null) {
            firstPointer.current = { x: e.clientX, y: e.clientY };
            return;
          }
          if (Math.hypot(e.clientX - p0.x, e.clientY - p0.y) < 4) return;
          hoverArmed.current = true;
        }
        // The cell under the pointer previews once armed (its pointerenter may have come before the pointer moved).
        if (hovered !== null) return;
        const cell = (e.target as Element | null)?.closest<HTMLElement>('[data-cell]')?.dataset.cell;
        const hit = cell === undefined ? null : cellById(cell);
        if (hit !== null) setHovered(hit.ease);
      }}
    >
      <header className={s.pickerHeader}>
        <h2 id={titleId} className={s.title}>
          {t(m.title)}
        </h2>
        <div className={s.search}>
          <Icon name="Search" size={14} />
          <input
            ref={searchRef}
            className={s.searchInput}
            type="search"
            role="combobox"
            aria-label={t(m.searchLabel)}
            aria-expanded={query !== ''}
            aria-controls={listId}
            aria-activedescendant={
              query !== '' && results[active] !== undefined ? `${listId}-${String(active)}` : undefined
            }
            placeholder={t(m.searchPlaceholder)}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActive(0);
              setTab('curves');
            }}
          />
        </div>
      </header>
      {stateHint !== null && <p className={s.hint}>{t(stateHint)}</p>}
      <div className={s.pickerBody}>
        <div className={s.pickerMain}>
          {bezierOk ? (
            <Tabs
              variant="pill"
              label={t(m.tabsLabel)}
              value={tab}
              onChange={(v) => {
                // Focus stays on the tab that was chosen (it does not go away).
                showTab(v === 'bezier' ? 'bezier' : 'curves', false);
              }}
              tabs={[
                { value: 'curves', label: t(m.tabCurves), icon: 'LayoutGrid' },
                { value: 'bezier', label: t(m.tabBezier), icon: 'Spline' },
              ]}
            />
          ) : (
            !bezierUseOk && (
              <p className={s.note}>
                {use === 'extraVar'
                  ? t(m.bakeHint)
                  : t(m.bezierHidden, { reason: bezierState.kind === 'disabled' ? tr(bezierState.reason) : '' })}
              </p>
            )
          )}
          {use === 'extraVar' && segments.length > 0 && (
            <Button
              size="sm"
              icon="Scissors"
              onClick={() => {
                finish(null);
                void services.registry.run('segments.bake', undefined);
              }}
            >
              {t(m.bakeOffer)}
            </Button>
          )}
          {tab === 'curves' && query !== '' && (
            <ul id={listId} role="listbox" aria-label={t(m.resultsLabel)} className={s.results}>
              {results.length === 0 && <li className={s.note}>{t(m.noResults, { query })}</li>}
              {results.map((cell, i) => {
                const state = easeState(cell.ease, use);
                return (
                  <li
                    key={cell.id}
                    id={`${listId}-${String(i)}`}
                    role="option"
                    aria-selected={i === active}
                    aria-disabled={state.kind === 'disabled' || undefined}
                    className={s.result}
                    data-active={i === active || undefined}
                    onPointerEnter={() => {
                      hover(cell.ease);
                      setActive(i);
                    }}
                    onPointerLeave={() => {
                      hover(null);
                    }}
                    onClick={() => {
                      pick(cell.ease);
                    }}
                  >
                    <CurveThumb
                      ease={cell.ease}
                      size={28}
                      animate={false}
                      showOvershoot={cell.entry?.overshoots === true}
                    />
                    <span>{cellName(cell, text)}</span>
                    {state.kind === 'disabled' && <span className={s.resultReason}>{tr(state.reason)}</span>}
                  </li>
                );
              })}
            </ul>
          )}
          {tab === 'curves' && query === '' && (
            <EasingGrid
              focus={focus}
              current={cellForEase(initial)?.id ?? null}
              showIds={showIds}
              stateOf={(cell) => easeState(cell.ease, use)}
              isFavourite={(cell) => isFav(cell.ease)}
              cellRef={(id, el) => {
                if (el === null) cellEls.current.delete(id);
                else cellEls.current.set(id, el);
              }}
              onFocusCell={(cell) => {
                if (cell.row !== focus.row || cell.col !== focus.col) goTo(cell, false);
              }}
              onHover={(cell) => {
                hover(cell === null ? null : cell.ease);
              }}
              onPick={(cell) => {
                const sliced = selected.kind === 'tween' && cell.ease.kind === 'tween' && selected.id === cell.ease.id;
                pick(sliced ? selected : cell.ease);
              }}
            />
          )}
          {tab === 'bezier' && (
            <BezierEditor
              value={bezier}
              onChange={(p) => {
                setBezier(p);
                setTouched(true);
              }}
              collision={collision}
              lineName={lineName}
              snap={snap}
              onSnapChange={setSnap}
              onNotice={(msg, kind) => {
                services.ui.toast({ key: m.notice, params: { text: msg } }, { kind });
              }}
            />
          )}
        </div>
        <aside className={s.pickerSide}>
          <div className={s.previewBox}>
            <span className={s.previewTitle}>{t(m.preview)}</span>
            <CurvePreview
              ease={candidate}
              compare={initial !== null && !sameEase(initial, candidate) ? initial : null}
              {...(info !== null && info.from !== null && info.to !== null ? { from: info.from, to: info.to } : {})}
              formatValue={formatValue}
              label={t(m.plotLabel, { name: easeName(candidate, text) })}
              progress={progress}
            />
            <p className={s.note}>
              {info !== null && info.from !== null && info.to !== null
                ? t(m.previewOf, {
                    prop: info.prop ?? 'none',
                    from: formatValue(info.from),
                    to: formatValue(info.to),
                    beats: formatValue(info.beats),
                  })
                : t(m.previewNoSegment)}
            </p>
            <p className={s.candidate}>
              <strong>{easeName(candidate, text)}</strong>
              {candState.kind === 'ok' && candState.baked === true && (
                <Badge kind="baked" label={{ key: m.bakedBadge }} tooltip={{ key: m.bakedTip }} />
              )}
              {candState.kind === 'ok' && candState.warning !== undefined && (
                <span className={s.warnText}>{tr(candState.warning)}</span>
              )}
              {candState.kind === 'disabled' && <span className={s.error}>{tr(candState.reason)}</span>}
            </p>
          </div>
          {showSlice && sliceEase !== null && (
            <SliceEditor
              key={sliceEase.id}
              ease={sliceEase}
              disabledReason={sliceReason}
              onChange={(e) => {
                setSelected(e);
                setTouched(true);
              }}
            />
          )}
          <div className={s.chips}>
            <span className={s.chipsTitle}>{t(m.recents)}</span>
            {recents.length === 0 ? <span className={s.muted}>-</span> : recents.map((e) => chip(e, `r-${easeKey(e)}`))}
          </div>
          <div className={s.chips}>
            <span className={s.chipsTitle}>
              <Icon name="Star" size={12} /> {t(m.favourites)}
            </span>
            {favourites.length === 0 ? (
              <span className={s.muted}>{t(m.noFavourites)}</span>
            ) : (
              favourites.map((e) => chip(e, `f-${easeKey(e)}`))
            )}
            <IconButton
              icon={isFav(chosen) ? 'StarOff' : 'Star'}
              size="sm"
              label={{ key: isFav(chosen) ? m.removeFavourite : m.addFavourite }}
              onClick={() => {
                favouriteToggle(chosen);
              }}
            />
          </div>
        </aside>
      </div>
      <footer className={s.pickerFooter}>
        <span className={s.count} role="status">
          {segments.length === 0
            ? ''
            : count.applies === 0
              ? t(m.noneApplies, { reason: count.reason === null ? '' : tr(count.reason) })
              : count.skipped > 0
                ? t(m.skipped, {
                    skipped: count.skipped,
                    count: infos.length,
                    reason: count.reason === null ? '' : tr(count.reason),
                  })
                : t(m.appliesTo, { count: count.applies })}
        </span>
        <span className={s.keysHint}>{t(level === 'full' ? m.keysHint : m.keysHintGuided)}</span>
        <div className={s.footerButtons}>
          <Button
            variant="ghost"
            onClick={() => {
              finish(null);
            }}
          >
            {t(m.cancel)}
          </Button>
          <Button
            variant="primary"
            disabledReason={chosenState.kind === 'disabled' ? chosenState.reason : null}
            onClick={() => {
              pick(chosen);
            }}
          >
            {t(m.apply)}
          </Button>
        </div>
      </footer>
    </div>
  );
}
